import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminNav from '../components/AdminNav';

// ─── Section definitions ──────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'all',        label: 'All',        actions: null },
  { key: 'auth',       label: 'Auth',       actions: ['LOGIN_SUCCESS', 'LOGIN_FAILURE', 'LOGOUT', 'CHANGE_PASSWORD'] },
  { key: 'assignment', label: 'Assignment', actions: ['ASSIGN_CERTS', 'EDIT_CERT'] },
  { key: 'batches',    label: 'Batches',    actions: ['GENERATE_BATCH', 'DELETE_BATCH'] },
  { key: 'users',      label: 'Users',      actions: ['CREATE_USER', 'DELETE_USER', 'CHANGE_USERNAME', 'RESET_PASSWORD'] },
];

const ACTION_LABELS = {
  LOGIN_SUCCESS:   'Signed in',
  LOGIN_FAILURE:   'Failed login',
  LOGOUT:          'Signed out',
  CHANGE_PASSWORD: 'Changed password',
  GENERATE_BATCH:  'Generated batch',
  DELETE_BATCH:    'Deleted batch',
  ASSIGN_CERTS:    'Assigned certs',
  EDIT_CERT:       'Edited cert',
  CREATE_USER:     'Created user',
  DELETE_USER:     'Deleted user',
  CHANGE_USERNAME: 'Changed username',
  RESET_PASSWORD:  'Reset password',
};

const ACTION_COLORS = {
  LOGIN_FAILURE:   { background: '#fef2f2', color: '#dc2626' },
  DELETE_BATCH:    { background: '#fef2f2', color: '#dc2626' },
  DELETE_USER:     { background: '#fef2f2', color: '#dc2626' },
  LOGIN_SUCCESS:   { background: '#f0fdf4', color: '#15803d' },
  GENERATE_BATCH:  { background: '#f0fdf4', color: '#15803d' },
  CREATE_USER:     { background: '#f0fdf4', color: '#15803d' },
  ASSIGN_CERTS:    { background: '#f0f9ff', color: '#0369a1' },
  EDIT_CERT:       { background: '#f0f9ff', color: '#0369a1' },
};

const ET_FMT = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const date = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' });
  return `${date} ${time} ET`;
};

const s = {
  page: { minHeight: '100vh', background: '#f8f8f6' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '40px 16px' },
  heading: { fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '20px' },
  // Section tabs
  tabs: { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #eee', paddingBottom: '0' },
  tab: (active) => ({
    padding: '8px 18px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    border: 'none', background: 'none',
    color: active ? '#111' : '#888',
    borderBottom: active ? '2px solid #111' : '2px solid transparent',
    marginBottom: '-2px',
  }),
  // Filters
  filters: {
    display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
    marginBottom: '16px', background: '#fff', borderRadius: '10px',
    padding: '14px 16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  },
  filterInput: {
    padding: '7px 12px', border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '13px', outline: 'none',
  },
  dateInput: {
    padding: '7px 10px', border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '13px', outline: 'none', colorScheme: 'light',
  },
  searchInput: {
    flex: 1, minWidth: '160px',
    padding: '7px 12px', border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '13px', outline: 'none',
  },
  clearBtn: {
    padding: '7px 14px', background: '#fff', color: '#555',
    border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '13px', cursor: 'pointer', fontWeight: '500',
  },
  filterLabel: { fontSize: '12px', color: '#888', fontWeight: '600', whiteSpace: 'nowrap' },
  // Multi-select
  msWrap: { position: 'relative' },
  msBtn: {
    padding: '7px 12px', border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '13px', background: '#fff', cursor: 'pointer',
    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px',
  },
  msBtnActive: {
    padding: '7px 12px', border: '1.5px solid #111', borderRadius: '8px',
    fontSize: '13px', background: '#111', color: '#fff', cursor: 'pointer',
    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px',
  },
  msPanel: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0,
    background: '#fff', border: '1.5px solid #ddd', borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    zIndex: 200, minWidth: '200px', padding: '8px 0',
  },
  msItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '7px 14px', cursor: 'pointer', fontSize: '13px', color: '#222',
  },
  msClearAll: {
    display: 'block', width: '100%', padding: '7px 14px',
    background: 'none', border: 'none', borderTop: '1px solid #eee',
    fontSize: '12px', color: '#888', cursor: 'pointer', textAlign: 'left',
    marginTop: '4px',
  },
  // Meta
  meta: { fontSize: '13px', color: '#888', marginBottom: '12px' },
  // Table
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.07)' },
  th: { padding: '11px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#888', borderBottom: '1px solid #eee', background: '#fafafa', letterSpacing: '0.04em', textTransform: 'uppercase' },
  td: { padding: '11px 16px', fontSize: '13px', color: '#222', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' },
  badge: (action) => ({
    display: 'inline-block', padding: '2px 9px', borderRadius: '4px',
    fontSize: '11px', fontWeight: '700',
    ...(ACTION_COLORS[action] || { background: '#f4f4f4', color: '#555' }),
  }),
  detailsText: { fontSize: '12px', color: '#666', marginTop: '3px', fontFamily: 'monospace' },
  // Pagination
  pagination: { display: 'flex', gap: '6px', alignItems: 'center', marginTop: '20px', justifyContent: 'center' },
  pageBtn: (active) => ({
    padding: '6px 12px', borderRadius: '6px', border: '1.5px solid',
    fontSize: '13px', fontWeight: '600', cursor: active ? 'default' : 'pointer',
    borderColor: active ? '#111' : '#ddd',
    background: active ? '#111' : '#fff',
    color: active ? '#fff' : '#555',
  }),
};

// ─── Multi-select dropdown ────────────────────────────────────────────────────
function MultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);

  const label = selected.length === 0 ? 'All actions ▾' : `${selected.length} action${selected.length > 1 ? 's' : ''} ▾`;
  const isActive = selected.length > 0;

  return (
    <div style={s.msWrap} ref={ref}>
      <button style={isActive ? s.msBtnActive : s.msBtn} onClick={() => setOpen((o) => !o)}>
        {label}
      </button>
      {open && (
        <div style={s.msPanel}>
          {options.map(({ value, label: lbl }) => (
            <label key={value} style={{ ...s.msItem, userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={() => toggle(value)}
                style={{ cursor: 'pointer' }}
              />
              <span style={s.badge(value)}>{lbl}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button style={s.msClearAll} onClick={() => { onChange([]); setOpen(false); }}>
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatDetails(details) {
  if (!details) return null;
  const entries = Object.entries(details).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return null;
  return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function History() {
  const [activeSection, setActiveSection] = useState('all');
  const [selectedActions, setSelectedActions] = useState([]);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const currentSection = SECTIONS.find((s) => s.key === activeSection);

  // Options shown in multi-select depend on the active section
  const actionOptions = (currentSection.actions || Object.keys(ACTION_LABELS)).map((a) => ({
    value: a,
    label: ACTION_LABELS[a] || a,
  }));

  // Build the effective actions list to send to the backend
  const getActionsParam = () => {
    if (selectedActions.length > 0) return selectedActions.join(',');
    if (currentSection.actions) return currentSection.actions.join(',');
    return '';
  };

  const fetchHistory = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    const actionsParam = getActionsParam();
    if (actionsParam) params.set('actions', actionsParam);
    if (search.trim()) params.set('search', search.trim());
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    fetch(`/api/admin/history?${params}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        setEntries(Array.isArray(data.entries) ? data.entries : []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, activeSection, selectedActions, search, from, to]); // eslint-disable-line

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const switchSection = (key) => {
    setActiveSection(key);
    setSelectedActions([]);
    setPage(1);
  };

  const clearAllFilters = () => {
    setSelectedActions([]);
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const hasFilters = selectedActions.length > 0 || search || from || to;

  return (
    <div style={s.page}>
      <AdminNav />

      <div style={s.content}>
        <div style={s.heading}>Action History</div>

        {/* Section tabs */}
        <div style={s.tabs}>
          {SECTIONS.map((sec) => (
            <button key={sec.key} style={s.tab(activeSection === sec.key)} onClick={() => switchSection(sec.key)}>
              {sec.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={s.filters}>
          <span style={s.filterLabel}>Actions</span>
          <MultiSelect
            options={actionOptions}
            selected={selectedActions}
            onChange={(v) => { setSelectedActions(v); setPage(1); }}
          />

          <span style={s.filterLabel}>From</span>
          <input
            style={s.dateInput}
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
          />

          <span style={s.filterLabel}>To</span>
          <input
            style={s.dateInput}
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
          />

          <input
            style={s.searchInput}
            type="text"
            placeholder="Search user or cert / batch #…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />

          {hasFilters && (
            <button style={s.clearBtn} onClick={clearAllFilters}>Clear filters</button>
          )}
        </div>

        <div style={s.meta}>
          {loading ? 'Loading…' : `${total.toLocaleString()} event${total !== 1 ? 's' : ''}${pages > 1 ? ` · Page ${page} of ${pages}` : ''}`}
        </div>

        <table style={s.table}>
          <thead>
            <tr>
              {['Timestamp (ET)', 'User', 'Action', 'Target', 'Details'].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && entries.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#888', padding: '48px' }}>
                  No events found.
                </td>
              </tr>
            ) : entries.map((e) => (
              <tr key={e.id}>
                <td style={{ ...s.td, whiteSpace: 'nowrap', color: '#666', fontSize: '12px' }}>{ET_FMT(e.timestamp)}</td>
                <td style={{ ...s.td, fontFamily: 'monospace' }}>{e.username}</td>
                <td style={s.td}>
                  <span style={s.badge(e.action)}>{ACTION_LABELS[e.action] || e.action}</span>
                </td>
                <td style={{ ...s.td, fontFamily: 'monospace', color: '#555', fontSize: '12px' }}>{e.target || '—'}</td>
                <td style={s.td}>
                  {e.details && <div style={s.detailsText}>{formatDetails(e.details)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pages > 1 && (
          <div style={s.pagination}>
            <button style={s.pageBtn(false)} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              ← Prev
            </button>
            {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
              const p = page <= 4 ? i + 1 : page - 3 + i;
              if (p < 1 || p > pages) return null;
              return (
                <button key={p} style={s.pageBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
              );
            })}
            <button style={s.pageBtn(false)} onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
